import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { cataloguePrice } from '@/lib/cataloguePricing';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Truck, Plus, Package, Search, Trash2, Download, Eye, Loader2, ScanBarcode, FileText, RefreshCw, CheckCircle2, MapPin, Store, Lock } from 'lucide-react';

var API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function DeliveryChallan() {
  var [activeTab, setActiveTab] = useState('history');
  var [dcs, setDcs] = useState([]);
  var [stats, setStats] = useState(null);
  var [loading, setLoading] = useState(true);
  var [products, setProducts] = useState([]);
  var [onboardings, setOnboardings] = useState([]);
  var [items, setItems] = useState([]);
  var [fromLocation, setFromLocation] = useState('Karunadu Products India Pvt Ltd');
  var [destSearch, setDestSearch] = useState('');
  var [showDestList, setShowDestList] = useState(false);
  var [selectedDest, setSelectedDest] = useState(null);
  var [reference, setReference] = useState('');
  var [remarks, setRemarks] = useState('');
  var [submitting, setSubmitting] = useState(false);
  var [searchTerm, setSearchTerm] = useState('');
  var [showSearch, setShowSearch] = useState(false);
  var [scanBarcode, setScanBarcode] = useState('');
  var scannerRef = useRef(null);
  var [selectedDCs, setSelectedDCs] = useState([]);
  var [viewDC, setViewDC] = useState(null);
  var [viewOpen, setViewOpen] = useState(false);

  var fetchData = useCallback(function() {
    setLoading(true);
    var token = localStorage.getItem('token');
    var h = { Authorization: 'Bearer ' + token };
    Promise.all([
      axios.get(API + '/delivery-challans', { headers: h }),
      axios.get(API + '/delivery-challans/stats', { headers: h }),
      axios.get(API + '/products', { headers: h }),
      axios.get(API + '/onboarding', { headers: h })
    ]).then(function(res) {
      setDcs(res[0].data); setStats(res[1].data); setProducts(res[2].data); setOnboardings(res[3].data); setLoading(false);
    }).catch(function() { toast.error('Failed to load data'); setLoading(false); });
  }, []);

  useEffect(function() { fetchData(); }, [fetchData]);

  var filteredProducts = products.filter(function(p) {
    var t = searchTerm.toLowerCase();
    return p.name.toLowerCase().indexOf(t) >= 0 || (p.product_id || '').toLowerCase().indexOf(t) >= 0 || (p.barcode || '').toLowerCase().indexOf(t) >= 0;
  }).slice(0, 10);

  var filteredDests = onboardings.filter(function(o) {
    // DC destinations: FICO franchises only (Oct 2026 requirement).
    var isFico = ((o.business_segment || '').toUpperCase().indexOf('FICO') >= 0)
              || ((o.franchise_id || '').toUpperCase().indexOf('FICO') >= 0);
    if (!isFico) return false;
    var t = destSearch.toLowerCase();
    if (!t) return true;
    return (o.name || '').toLowerCase().indexOf(t) >= 0 || (o.franchise_id || '').toLowerCase().indexOf(t) >= 0 || (o.contact_person || '').toLowerCase().indexOf(t) >= 0 || (o.district || '').toLowerCase().indexOf(t) >= 0;
  }).slice(0, 15);

  function pickDestination(o) {
    setSelectedDest(o);
    setDestSearch(o.name + (o.franchise_id ? ' (' + o.franchise_id + ')' : ''));
    setShowDestList(false);
  }

  function clearDestination() {
    setSelectedDest(null);
    setDestSearch('');
  }

  function addProduct(product, atTop) {
    if (items.find(function(i) { return i.product_id === product.id; })) { toast.info('Already added'); return; }
    var mrp = parseFloat(product.mrp || 0);
    var sp = parseFloat(product.outward_selling_price || product.selling_price || 0);
    var newItem = {
      product_id: product.id,
      product_name: product.name,
      product_sku: product.product_id,
      quantity: 1,
      unit: product.measurement_unit || 'PCS',
      available_stock: product.current_stock || 0,
      mrp: mrp,
      selling_price: sp,
      total_value: sp,
      // Batch picker state — populated async right after add.
      batches: [],
      batch_id: null,
      batch_number: null
    };
    // Barcode scans land on TOP so the newest scan is always visible (Oct 2026).
    setItems(atTop ? [newItem].concat(items) : items.concat(newItem));
    setSearchTerm(''); setShowSearch(false);
    // Fetch batches for this product so the dropdown can render.
    axios.get(API + '/inventory/batches?only_available=true&product_id=' + encodeURIComponent(product.id), {
      headers: { Authorization: 'Bearer ' + localStorage.getItem('token') }
    }).then(function(res) {
      var list = res.data || [];
      setItems(function(cur) {
        // Locate by product_id (NOT index) — rows may have been reordered by scans.
        var rowIdx = cur.findIndex(function(i) { return i.product_id === product.id; });
        if (rowIdx < 0) return cur;
        var n = cur.slice();
        var auto = list.length === 1 ? list[0] : null;
        n[rowIdx] = Object.assign({}, n[rowIdx], {
          batches: list,
          batch_id: auto ? auto.id : null,
          batch_number: auto ? auto.batch_number : null,
          available_stock: auto ? auto.current_stock : (n[rowIdx].available_stock || 0)
        });
        return n;
      });
    }).catch(function() { /* leave empty - product without batches falls back to master stock */ });
  }

  function pickBatch(idx, batchId) {
    var n = items.slice();
    var batch = (n[idx].batches || []).find(function(b) { return b.id === batchId; });
    if (!batch) return;
    n[idx] = Object.assign({}, n[idx], {
      batch_id: batch.id,
      batch_number: batch.batch_number,
      available_stock: batch.current_stock,
      mrp: batch.mrp || n[idx].mrp
    });
    setItems(n);
  }

  function updateQty(idx, qty) {
    var n = items.slice();
    var q = parseFloat(qty) || 0;
    n[idx] = Object.assign({}, n[idx], { quantity: q, total_value: +(q * (n[idx].selling_price || 0)).toFixed(2) });
    setItems(n);
  }

  function updateSellingPrice(idx, val) {
    var n = items.slice();
    var sp = parseFloat(val) || 0;
    n[idx] = Object.assign({}, n[idx], { selling_price: sp, total_value: +(sp * (n[idx].quantity || 0)).toFixed(2), price_manual_override: true });
    setItems(n);
    setPricesTouched(true);
  }

  function updateMrp(idx, val) {
    var n = items.slice();
    n[idx] = Object.assign({}, n[idx], { mrp: parseFloat(val) || 0 });
    setItems(n);
    setPricesTouched(true);
  }

  function removeItem(idx) { setItems(items.filter(function(_, i) { return i !== idx; })); }

  function handleBarcodeScan() {
    var code = scanBarcode.trim(); if (!code) return;
    var token = localStorage.getItem('token');
    axios.get(API + '/products/barcode/' + encodeURIComponent(code), { headers: { Authorization: 'Bearer ' + token } })
      .then(function(res) {
        var p = res.data;
        var existing = items.findIndex(function(i) { return i.product_id === p.id; });
        if (existing >= 0) {
          var n = items.slice();
          var nq = n[existing].quantity + 1;
          var bumped = Object.assign({}, n[existing], { quantity: nq, total_value: +(nq * (n[existing].selling_price || 0)).toFixed(2) });
          n.splice(existing, 1);
          n.unshift(bumped); // most recent scan stays on top
          setItems(n);
          toast.success(p.name + ' qty +1');
        } else { addProduct(p, true); toast.success('Added: ' + p.name); }
      }).catch(function() { toast.error('Barcode not found'); });
    setScanBarcode(''); if (scannerRef.current) scannerRef.current.focus();
  }

  var [editingDC, setEditingDC] = useState(null);  // when set, create form is in EDIT mode for this draft
  var [pricesTouched, setPricesTouched] = useState(false);  // true when user edited MRP / B2B Selling Price

  // Compare current item prices against the Master Catalogue; return lines that differ.
  function getPriceDiffs() {
    var diffs = [];
    items.forEach(function(i) {
      var prod = products.find(function(p) { return p.id === i.product_id; });
      if (!prod) return;
      var masterMrp = parseFloat(prod.mrp || 0);
      var masterSp = parseFloat(prod.outward_selling_price || prod.selling_price || 0);
      if (Math.abs((i.mrp || 0) - masterMrp) > 0.001 || Math.abs((i.selling_price || 0) - masterSp) > 0.001) {
        diffs.push({ product_id: i.product_id, product_name: i.product_name, mrp: i.mrp || 0, selling_price: i.selling_price || 0 });
      }
    });
    return diffs;
  }

  function handleSubmit(finalize) {
    if (!selectedDest && !editingDC) { toast.error('Please select a destination from Onboarded stores'); return; }
    if (items.length === 0) { toast.error('Add at least one item'); return; }
    for (var i = 0; i < items.length; i++) {
      if (items[i].quantity <= 0) { toast.error('Quantity must be > 0 for ' + items[i].product_name); return; }
      // TEMP: oversell allowed during stock correction — soft warning instead of hard block
      if (items[i].quantity > items[i].available_stock) { toast.warning('Low stock for ' + items[i].product_name + ' (Available: ' + items[i].available_stock + ') — proceeding anyway'); }
      // When the product has 2+ available batches, the user MUST pick one
      // (single-batch products auto-pick on add). Per packed batch rules.
      if ((items[i].batches || []).length > 1 && !items[i].batch_id) {
        toast.error('Pick a batch for ' + items[i].product_name);
        return;
      }
    }
    setSubmitting(true);
    var token = localStorage.getItem('token');

    // Master Catalogue price-sync prompt: fires when the user modified MRP /
    // B2B Selling Price and the values differ from the current catalogue.
    var priceDiffs = pricesTouched ? getPriceDiffs() : [];
    var syncCatalogue = false;
    if (priceDiffs.length > 0) {
      syncCatalogue = window.confirm(
        'The MRP and/or B2B Selling Price has been modified. Do you want to update these values in the Master Catalogue as the latest active pricing?\n\n' +
        'Products: ' + priceDiffs.map(function(d) { return d.product_name; }).join(', ') + '\n\n' +
        'OK — Update Master Catalogue (syncs instantly across Invoices, DCs, Product Search & Inventory. Stock/FIFO untouched)\n' +
        'Cancel — Apply the new price to this DC only'
      );
    }

    var addr = selectedDest
      ? [selectedDest.address, selectedDest.district, selectedDest.state, selectedDest.pincode].filter(Boolean).join(', ')
      : (editingDC.to_address || '');
    var payload = {
      from_location: fromLocation,
      to_location: selectedDest ? selectedDest.name : (editingDC.to_location || ''),
      to_franchise_id: selectedDest ? selectedDest.franchise_id : (editingDC.to_franchise_id || null),
      to_onboarding_id: selectedDest ? selectedDest.id : (editingDC.to_onboarding_id || null),
      to_address: addr,
      to_contact_person: selectedDest ? selectedDest.contact_person : (editingDC.to_contact_person || null),
      to_phone: selectedDest ? (selectedDest.mobile || selectedDest.phone) : (editingDC.to_phone || null),
      items: items.map(function(i) { return { product_id: i.product_id, product_name: i.product_name, product_sku: i.product_sku, quantity: i.quantity, unit: i.unit, mrp: i.mrp, selling_price: i.selling_price, total_value: i.total_value, batch_id: i.batch_id || null, batch_number: i.batch_number || null, price_manual_override: !!i.price_manual_override }; }),
      reference: reference || null, remarks: remarks || null
    };
    var headers = { Authorization: 'Bearer ' + token };
    var req;
    if (editingDC) {
      // Editing flow:
      // - Draft → PUT keeps status=draft. If user clicked Save & Dispatch
      //   on a draft, chain a finalize call right after.
      // - Dispatched → PUT reverses old stock and re-applies new stock
      //   atomically. "Save & Dispatch" on a dispatched DC is just save
      //   (it's already dispatched), so no finalize chain.
      var isDispatchedEdit = editingDC.status === 'dispatched';
      req = axios.put(API + '/delivery-challans/' + editingDC.id, payload, { headers: headers })
        .then(function(res) {
          if (finalize && !isDispatchedEdit) {
            return axios.post(API + '/delivery-challans/' + editingDC.id + '/finalize', {}, { headers: headers });
          }
          return res;
        });
    } else {
      req = axios.post(API + '/delivery-challans?finalize=' + (finalize ? 'true' : 'false'), payload, { headers: headers });
    }
    req
      .then(function(res) {
        var dcId = editingDC ? editingDC.id : (res.data && res.data.id);
        var dcNumber = (res.data && res.data.dc_number) || (editingDC && editingDC.dc_number);
        var syncReq = (syncCatalogue && dcId)
          ? axios.post(API + '/delivery-challans/' + dcId + '/sync-prices-to-catalogue',
              { items: priceDiffs.map(function(d) { return { product_id: d.product_id, mrp: d.mrp, selling_price: d.selling_price }; }) },
              { headers: headers })
            .then(function(sr) { toast.success(sr.data.message || 'Master Catalogue prices updated'); })
            .catch(function() { toast.error('DC saved but Master Catalogue price sync failed'); })
          : Promise.resolve();
        return syncReq.then(function() {
          var label = finalize ? 'dispatched' : 'saved as draft';
          toast.success('DC ' + dcNumber + ' ' + label);
          setSubmitting(false); setActiveTab('history'); fetchData();
          setItems([]); clearDestination(); setReference(''); setRemarks('');
          setEditingDC(null); setPricesTouched(false);
        });
      })
      .catch(function(err) { toast.error(err.response && err.response.data ? err.response.data.detail : 'Failed'); setSubmitting(false); });
  }

  function startEditDraft(dc) {
    // Hydrate the create form with the DC's data so the user can edit & re-save.
    // Only DRAFT DCs are editable — finalized DCs are locked (Jul 2026).
    setEditingDC(dc);
    setFromLocation(dc.from_location || 'Karunadu Products India Pvt Ltd');
    // Best-effort destination rehydration from the onboarding list.
    var dest = onboardings.find(function(o) { return o.id === dc.to_onboarding_id; })
            || onboardings.find(function(o) { return o.franchise_id === dc.to_franchise_id; });
    if (dest) {
      setSelectedDest(dest); setDestSearch(dest.name);
    } else {
      // Fallback — keep destination string visible so user can re-pick.
      setSelectedDest(null); setDestSearch(dc.to_location || '');
    }
    // Hydrate items — pull `batches` + `available_stock` from the products array.
    var hydrated = (dc.items || []).map(function(it) {
      var prod = products.find(function(p) { return p.id === it.product_id; });
      var catPrice = it.price_manual_override ? null : cataloguePrice(prod);
      var sp = catPrice || it.selling_price || 0;
      return {
        product_id: it.product_id,
        product_name: it.product_name,
        product_sku: it.product_sku,
        quantity: it.quantity,
        unit: it.unit || 'PCS',
        mrp: it.mrp || 0,
        selling_price: sp,
        total_value: catPrice ? +(catPrice * (it.quantity || 0)).toFixed(2) : (it.total_value || 0),
        price_manual_override: !!it.price_manual_override,
        available_stock: prod ? (prod.current_stock || 0) : 0,
        batches: prod && prod.batches ? prod.batches : [],
        batch_id: it.batch_id || null,
        batch_number: it.batch_number || null,
      };
    });
    setItems(hydrated);
    setReference(dc.reference || '');
    setRemarks(dc.remarks || '');
    setPricesTouched(false);
    setActiveTab('create');
    toast.info('Editing draft ' + dc.dc_number);
  }

  function cancelEdit() {
    setEditingDC(null); setPricesTouched(false);
    setItems([]); clearDestination(); setReference(''); setRemarks('');
  }

  function handleFinalize(id) {
    if (!window.confirm('Dispatch this DC now? Stock will be deducted. This cannot be undone.')) return;
    var token = localStorage.getItem('token');
    axios.post(API + '/delivery-challans/' + id + '/finalize', {}, { headers: { Authorization: 'Bearer ' + token } })
      .then(function(res) { toast.success('DC ' + res.data.dc_number + ' dispatched'); fetchData(); })
      .catch(function(err) { toast.error(err.response && err.response.data ? err.response.data.detail : 'Finalize failed'); });
  }

  function handleDelete(id) {
    if (!window.confirm('Delete this draft DC? Its reserved stock will be released back to available inventory.')) return;
    var token = localStorage.getItem('token');
    axios.delete(API + '/delivery-challans/' + id, { headers: { Authorization: 'Bearer ' + token } })
      .then(function() { toast.success('DC deleted'); fetchData(); })
      .catch(function(err) { toast.error(err.response && err.response.data ? err.response.data.detail : 'Delete failed'); });
  }

  function handleReceive(id) {
    var token = localStorage.getItem('token');
    axios.post(API + '/delivery-challans/' + id + '/receive', {}, { headers: { Authorization: 'Bearer ' + token } })
      .then(function() { toast.success('DC marked as received'); fetchData(); }).catch(function(e) { toast.error(e.response && e.response.data ? e.response.data.detail : 'Failed'); });
  }

  function downloadPDF(id, dcNumber) {
    var token = localStorage.getItem('token');
    axios.get(API + '/delivery-challans/' + id + '/pdf', {
      headers: { Authorization: 'Bearer ' + token },
      responseType: 'blob'
    }).then(function(res) {
      var url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      var link = document.createElement('a');
      link.href = url;
      link.download = 'DC_' + (dcNumber || id) + '.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(function() { window.URL.revokeObjectURL(url); }, 100);
    }).catch(function() { toast.error('Failed to download PDF'); });
  }

  function toggleSelect(id) { setSelectedDCs(function(p) { return p.indexOf(id) >= 0 ? p.filter(function(x) { return x !== id; }) : p.concat(id); }); }
  function toggleSelectAll() { setSelectedDCs(selectedDCs.length === dcs.length ? [] : dcs.map(function(d) { return d.id; })); }
  function handleBulkDelete() {
    if (!window.confirm('Delete ' + selectedDCs.length + ' DC(s)?')) return;
    var token = localStorage.getItem('token');
    axios.post(API + '/delivery-challans/bulk-delete', { ids: selectedDCs }, { headers: { Authorization: 'Bearer ' + token } })
      .then(function() { toast.success('Deleted'); setSelectedDCs([]); fetchData(); }).catch(function() { toast.error('Failed'); });
  }

  var totalQty = items.reduce(function(s, i) { return s + (i.quantity || 0); }, 0);
  var totalValue = items.reduce(function(s, i) { return s + (i.total_value || 0); }, 0);

  return React.createElement('div', { className: 'space-y-6', 'data-testid': 'delivery-challan-page' },
    React.createElement('div', { className: 'flex items-center justify-between' },
      React.createElement('div', { className: 'flex items-center gap-3' },
        React.createElement(Truck, { className: 'w-6 h-6 text-indigo-600' }),
        React.createElement('div', null,
          React.createElement('h1', { className: 'text-2xl font-bold text-slate-800' }, 'Delivery Challans'),
          React.createElement('p', { className: 'text-sm text-slate-500' }, 'Stock transfer between locations'))),
      React.createElement(Button, { onClick: function() { setActiveTab('create'); }, className: 'gap-2 bg-indigo-600 hover:bg-indigo-700', 'data-testid': 'create-dc-btn' }, React.createElement(Plus, { className: 'w-4 h-4' }), 'Create DC')),

    stats && React.createElement('div', { className: 'grid grid-cols-2 md:grid-cols-5 gap-3' },
      React.createElement(Card, null, React.createElement(CardContent, { className: 'pt-4 pb-3' }, React.createElement('p', { className: 'text-xs text-gray-500 uppercase' }, 'Total DCs'), React.createElement('p', { className: 'text-2xl font-bold' }, stats.total))),
      React.createElement(Card, { className: 'border-slate-200 bg-slate-50' }, React.createElement(CardContent, { className: 'pt-4 pb-3' }, React.createElement('p', { className: 'text-xs text-slate-600 uppercase' }, 'Drafts'), React.createElement('p', { className: 'text-2xl font-bold text-slate-700' }, stats.draft || 0))),
      React.createElement(Card, { className: 'border-blue-200 bg-blue-50' }, React.createElement(CardContent, { className: 'pt-4 pb-3' }, React.createElement('p', { className: 'text-xs text-blue-600 uppercase' }, 'Dispatched'), React.createElement('p', { className: 'text-2xl font-bold text-blue-700' }, stats.dispatched))),
      React.createElement(Card, { className: 'border-green-200 bg-green-50' }, React.createElement(CardContent, { className: 'pt-4 pb-3' }, React.createElement('p', { className: 'text-xs text-green-600 uppercase' }, 'Received'), React.createElement('p', { className: 'text-2xl font-bold text-green-700' }, stats.received))),
      React.createElement(Card, { className: 'border-indigo-200 bg-indigo-50' }, React.createElement(CardContent, { className: 'pt-4 pb-3' }, React.createElement('p', { className: 'text-xs text-indigo-600 uppercase' }, 'Total Value'), React.createElement('p', { className: 'text-2xl font-bold text-indigo-700' }, '₹' + (stats.total_value || 0).toLocaleString('en-IN'))))),

    React.createElement(Tabs, { value: activeTab, onValueChange: setActiveTab },
      React.createElement(TabsList, { className: 'grid w-full max-w-md grid-cols-2' },
        React.createElement(TabsTrigger, { value: 'history', className: 'gap-2' }, React.createElement(FileText, { className: 'w-4 h-4' }), 'DC History'),
        React.createElement(TabsTrigger, { value: 'create', className: 'gap-2', 'data-testid': 'tab-create-dc' }, React.createElement(Plus, { className: 'w-4 h-4' }), 'Create DC')),

      React.createElement(TabsContent, { value: 'create', className: 'space-y-4 mt-4' },
        renderCreateForm({
          fromLocation: fromLocation, setFromLocation: setFromLocation,
          destSearch: destSearch, setDestSearch: setDestSearch,
          showDestList: showDestList, setShowDestList: setShowDestList,
          selectedDest: selectedDest, pickDestination: pickDestination, clearDestination: clearDestination,
          filteredDests: filteredDests, onboardings: onboardings,
          reference: reference, setReference: setReference,
          remarks: remarks, setRemarks: setRemarks,
          items: items, updateQty: updateQty, updateSellingPrice: updateSellingPrice, updateMrp: updateMrp, removeItem: removeItem,
          pickBatch: pickBatch,
          searchTerm: searchTerm, setSearchTerm: setSearchTerm, showSearch: showSearch, setShowSearch: setShowSearch,
          filteredProducts: filteredProducts, addProduct: addProduct,
          scanBarcode: scanBarcode, setScanBarcode: setScanBarcode, scannerRef: scannerRef, handleBarcodeScan: handleBarcodeScan,
          totalQty: totalQty, totalValue: totalValue, submitting: submitting,
          handleSubmit: handleSubmit, editingDC: editingDC, cancelEdit: cancelEdit
        })),

      React.createElement(TabsContent, { value: 'history', className: 'mt-4' },
        renderHistory(dcs, loading, selectedDCs, toggleSelect, toggleSelectAll, handleBulkDelete, handleDelete, handleReceive, downloadPDF, setViewDC, setViewOpen, fetchData, startEditDraft, handleFinalize))),

    React.createElement(DCViewDialog, { dc: viewDC, open: viewOpen, onOpenChange: setViewOpen }));
}

function renderCreateForm(p) {
  var dest = p.selectedDest;
  var destAddr = dest ? [dest.address, dest.district, dest.state, dest.pincode].filter(Boolean).join(', ') : '';

  return React.createElement(React.Fragment, null,
    React.createElement(Card, null,
      React.createElement(CardHeader, { className: 'pb-3' }, React.createElement(CardTitle, { className: 'text-lg flex items-center gap-2' }, React.createElement(Truck, { className: 'w-5 h-5 text-indigo-600' }), 'Transfer Details')),
      React.createElement(CardContent, null,
        React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-2 gap-4' },
          React.createElement('div', null,
            React.createElement(Label, null, 'From Location (Source) *'),
            React.createElement(Input, { value: p.fromLocation, onChange: function(e) { p.setFromLocation(e.target.value); }, placeholder: 'Karunadu Products India Pvt Ltd', className: 'mt-1', 'data-testid': 'dc-from-location' })),

          React.createElement('div', { className: 'relative' },
            React.createElement(Label, null, 'To Location (Onboarded Store) *'),
            React.createElement('div', { className: 'relative mt-1' },
              React.createElement(Store, { className: 'absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' }),
              React.createElement(Input, {
                value: p.destSearch,
                onChange: function(e) { p.setDestSearch(e.target.value); p.setShowDestList(true); if (p.selectedDest) p.clearDestination(); },
                onFocus: function() { p.setShowDestList(true); },
                placeholder: p.onboardings.length === 0 ? 'No stores onboarded yet' : 'Search franchise / store / branch...',
                className: 'pl-10 pr-8',
                'data-testid': 'dc-to-location'
              }),
              p.selectedDest && React.createElement('button', { type: 'button', onClick: p.clearDestination, className: 'absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-red-500 text-xs' }, '✕')),
            p.showDestList && p.filteredDests.length > 0 && React.createElement('div', { className: 'absolute z-30 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto' },
              p.filteredDests.map(function(o) {
                return React.createElement('div', {
                  key: o.id,
                  onClick: function() { p.pickDestination(o); },
                  className: 'p-3 hover:bg-indigo-50 cursor-pointer border-b',
                  'data-testid': 'dc-dest-option-' + o.id
                },
                  React.createElement('div', { className: 'flex items-center justify-between' },
                    React.createElement('p', { className: 'font-medium text-slate-800' }, o.name),
                    React.createElement(Badge, { variant: 'outline', className: 'text-xs' }, o.franchise_id || '')),
                  React.createElement('p', { className: 'text-xs text-slate-500 mt-1' },
                    React.createElement(MapPin, { className: 'w-3 h-3 inline mr-1' }),
                    [o.district, o.state].filter(Boolean).join(', ')),
                  o.contact_person && React.createElement('p', { className: 'text-xs text-slate-400' }, o.contact_person, ' · ', o.mobile || o.phone || ''));
              })))),

        dest && React.createElement('div', { className: 'mt-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg', 'data-testid': 'dc-dest-preview' },
          React.createElement('div', { className: 'flex items-start gap-2' },
            React.createElement(MapPin, { className: 'w-4 h-4 text-indigo-600 mt-0.5' }),
            React.createElement('div', { className: 'flex-1 text-sm' },
              React.createElement('p', { className: 'font-semibold text-slate-800' }, dest.name, dest.franchise_id ? ' · ' + dest.franchise_id : ''),
              React.createElement('p', { className: 'text-slate-600' }, destAddr),
              React.createElement('p', { className: 'text-xs text-slate-500 mt-1' },
                dest.contact_person ? 'Contact: ' + dest.contact_person + ' · ' : '',
                dest.mobile || dest.phone || '',
                dest.business_segment ? ' · ' + dest.business_segment : '')))),

        React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-2 gap-4 mt-4' },
          React.createElement('div', null, React.createElement(Label, null, 'Reference (Optional)'), React.createElement(Input, { value: p.reference, onChange: function(e) { p.setReference(e.target.value); }, placeholder: 'PO/Order reference...', className: 'mt-1' })),
          React.createElement('div', null, React.createElement(Label, null, 'Remarks (Optional)'), React.createElement(Textarea, { value: p.remarks, onChange: function(e) { p.setRemarks(e.target.value); }, placeholder: 'Notes...', className: 'mt-1', rows: 1 }))))),

    React.createElement(Card, null,
      React.createElement(CardHeader, { className: 'pb-3' }, React.createElement(CardTitle, { className: 'text-lg flex items-center justify-between' }, React.createElement('span', { className: 'flex items-center gap-2' }, React.createElement(Package, { className: 'w-5 h-5 text-orange-600' }), 'Items'), React.createElement(Badge, null, p.items.length + ' items'))),
      React.createElement(CardContent, { className: 'space-y-3' },
        React.createElement('div', { className: 'p-3 bg-slate-50 rounded-lg border' },
          React.createElement('div', { className: 'flex items-center gap-2 mb-2' }, React.createElement(ScanBarcode, { className: 'w-4 h-4 text-emerald-600' }), React.createElement('span', { className: 'text-sm font-medium' }, 'Barcode Scanner')),
          React.createElement('div', { className: 'flex gap-2' },
            React.createElement('div', { className: 'relative flex-1' }, React.createElement(ScanBarcode, { className: 'absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' }), React.createElement(Input, { ref: p.scannerRef, value: p.scanBarcode, onChange: function(e) { p.setScanBarcode(e.target.value); }, onKeyDown: function(e) { if (e.key === 'Enter') p.handleBarcodeScan(); }, placeholder: 'Scan barcode...', className: 'pl-10 font-mono', 'data-testid': 'dc-barcode-input' })),
            React.createElement(Button, { onClick: p.handleBarcodeScan, disabled: !p.scanBarcode.trim(), className: 'bg-emerald-600 hover:bg-emerald-700 gap-1' }, React.createElement(ScanBarcode, { className: 'w-4 h-4' }), 'Add'))),
        React.createElement('div', { className: 'relative' },
          React.createElement('div', { className: 'relative' }, React.createElement(Search, { className: 'absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' }), React.createElement(Input, { value: p.searchTerm, onChange: function(e) { p.setSearchTerm(e.target.value); p.setShowSearch(true); }, onFocus: function() { p.setShowSearch(true); }, placeholder: 'Search product by name, SKU, barcode...', className: 'pl-10', 'data-testid': 'dc-product-search' })),
          p.showSearch && p.searchTerm.length >= 2 && p.filteredProducts.length > 0 && React.createElement('div', { className: 'absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto' },
            p.filteredProducts.map(function(pr) { return React.createElement('div', { key: pr.id, onClick: function() { p.addProduct(pr); }, className: 'p-3 hover:bg-blue-50 cursor-pointer border-b', 'data-testid': 'dc-product-option-' + pr.id },
              React.createElement('div', { className: 'flex justify-between items-start' },
                React.createElement('div', null,
                  React.createElement('p', { className: 'font-medium' }, pr.name),
                  React.createElement('p', { className: 'text-xs text-gray-400' }, pr.product_id, ' | Stock: ', pr.current_stock || 0)),
                React.createElement('div', { className: 'text-right text-xs' },
                  React.createElement('p', { className: 'text-slate-500' }, 'MRP ₹', pr.mrp || 0),
                  React.createElement('p', { className: 'font-medium text-indigo-600' }, 'SP ₹', pr.outward_selling_price || 0)))); }))),

        p.items.length > 0 && React.createElement('div', { className: 'border rounded-lg overflow-auto max-h-[55vh]' },
          React.createElement('table', { className: 'w-full text-sm' },
            React.createElement('thead', { className: 'bg-indigo-50 sticky top-0 z-10 shadow-sm' },
              React.createElement('tr', null,
                React.createElement('th', { className: 'px-3 py-2 text-left font-semibold' }, 'Product'),
                React.createElement('th', { className: 'px-3 py-2 text-center font-semibold' }, 'Batch'),
                React.createElement('th', { className: 'px-3 py-2 text-center font-semibold' }, 'Avail'),
                React.createElement('th', { className: 'px-3 py-2 text-center font-semibold' }, 'Qty'),
                React.createElement('th', { className: 'px-3 py-2 text-center font-semibold' }, 'Unit'),
                React.createElement('th', { className: 'px-3 py-2 text-right font-semibold' }, 'MRP'),
                React.createElement('th', { className: 'px-3 py-2 text-right font-semibold' }, 'Selling Price'),
                React.createElement('th', { className: 'px-3 py-2 text-right font-semibold' }, 'Total Value'),
                React.createElement('th', { className: 'px-3 py-2 w-10' }))),
            React.createElement('tbody', { className: 'divide-y' },
              p.items.map(function(item, idx) {
                var overstock = item.quantity > item.available_stock;
                var batchList = item.batches || [];
                var needsBatch = batchList.length > 1 && !item.batch_id;
                return React.createElement('tr', { key: item.product_id, className: overstock ? 'bg-red-50' : (needsBatch ? 'bg-amber-50' : 'hover:bg-gray-50') },
                  React.createElement('td', { className: 'px-3 py-2' }, React.createElement('p', { className: 'font-medium' }, item.product_name), React.createElement('p', { className: 'text-xs text-gray-400' }, item.product_sku)),
                  React.createElement('td', { className: 'px-3 py-2 text-center' },
                    batchList.length === 0
                      ? React.createElement('span', { className: 'text-xs text-gray-400 italic' }, 'No batches')
                      : React.createElement('select', {
                          value: item.batch_id || '',
                          onChange: function(e) { p.pickBatch(idx, e.target.value); },
                          className: 'h-8 text-xs border border-gray-300 rounded px-2 ' + (needsBatch ? 'border-amber-500 ring-1 ring-amber-300' : ''),
                          'data-testid': 'dc-batch-' + idx
                        },
                          React.createElement('option', { value: '' }, '— pick batch —'),
                          batchList.map(function(b) {
                            return React.createElement('option', { key: b.id, value: b.id },
                              b.batch_number + ' · MRP ₹' + (b.mrp || 0) + ' · qty ' + (b.current_stock || 0));
                          })
                        ),
                    needsBatch && React.createElement('p', { className: 'text-xs text-amber-700 mt-0.5' }, 'Pick a batch')
                  ),
                  React.createElement('td', { className: 'px-3 py-2 text-center font-semibold' }, item.available_stock),
                  React.createElement('td', { className: 'px-3 py-2 text-center' },
                    React.createElement(Input, { type: 'number', min: 1, max: item.available_stock, value: item.quantity, onChange: function(e) { p.updateQty(idx, e.target.value); }, className: 'h-8 w-20 mx-auto text-center' + (overstock ? ' border-red-500' : ''), 'data-testid': 'dc-qty-' + idx }),
                    overstock && React.createElement('p', { className: 'text-xs text-red-500 mt-0.5' }, 'Exceeds stock')),
                  React.createElement('td', { className: 'px-3 py-2 text-center text-xs' }, item.unit),
                  React.createElement('td', { className: 'px-3 py-2 text-right' },
                    React.createElement(Input, { type: 'number', step: '0.01', min: 0, value: item.mrp, onChange: function(e) { p.updateMrp(idx, e.target.value); }, className: 'h-8 w-24 ml-auto text-right', 'data-testid': 'dc-mrp-' + idx })),
                  React.createElement('td', { className: 'px-3 py-2 text-right' },
                    React.createElement(Input, { type: 'number', step: '0.01', min: 0, value: item.selling_price, onChange: function(e) { p.updateSellingPrice(idx, e.target.value); }, className: 'h-8 w-24 ml-auto text-right', 'data-testid': 'dc-sp-' + idx })),
                  React.createElement('td', { className: 'px-3 py-2 text-right font-semibold text-indigo-700', 'data-testid': 'dc-total-' + idx }, '₹', (item.total_value || 0).toFixed(2)),
                  React.createElement('td', { className: 'px-3 py-2' }, React.createElement(Button, { variant: 'ghost', size: 'sm', onClick: function() { p.removeItem(idx); }, className: 'text-red-500 hover:text-red-700' }, React.createElement(Trash2, { className: 'w-4 h-4' }))));
              }),
              React.createElement('tr', { className: 'bg-indigo-50 font-semibold' },
                React.createElement('td', { className: 'px-3 py-2 text-right', colSpan: 3 }, 'Totals'),
                React.createElement('td', { className: 'px-3 py-2 text-center' }, p.totalQty),
                React.createElement('td', { colSpan: 3 }),
                React.createElement('td', { className: 'px-3 py-2 text-right text-indigo-700', 'data-testid': 'dc-grand-total' }, '₹', p.totalValue.toFixed(2)),
                React.createElement('td'))))),

        p.items.length > 0 && React.createElement('div', { className: 'flex items-center justify-between p-3 bg-indigo-50 rounded-lg border border-indigo-200 flex-wrap gap-2' },
          React.createElement('span', { className: 'text-sm' }, 'Items: ', React.createElement('strong', null, p.items.length), ' | Total Qty: ', React.createElement('strong', null, p.totalQty), ' | Total Value: ', React.createElement('strong', null, '₹', p.totalValue.toFixed(2)),
            p.editingDC && React.createElement('span', { className: 'ml-2 text-xs font-semibold ' + (p.editingDC.status === 'dispatched' ? 'text-blue-700 bg-blue-100' : 'text-amber-700 bg-amber-100') + ' px-2 py-0.5 rounded' },
              'EDITING ' + (p.editingDC.status === 'dispatched' ? 'DISPATCHED' : 'DRAFT') + ' ' + p.editingDC.dc_number)),
          React.createElement('div', { className: 'flex gap-2 items-center' },
            p.editingDC && React.createElement(Button, { onClick: p.cancelEdit, variant: 'outline', size: 'sm', 'data-testid': 'cancel-edit-btn' }, 'Cancel'),
            React.createElement(Button, {
              onClick: function() { p.handleSubmit(false); },
              disabled: p.submitting || p.items.length === 0,
              variant: 'outline',
              className: 'gap-2 border-slate-300',
              'data-testid': 'save-draft-dc-btn'
            },
              p.submitting ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : React.createElement(FileText, { className: 'w-4 h-4' }),
              'Save as Draft'),
            React.createElement(Button, {
              onClick: function() { p.handleSubmit(true); },
              disabled: p.submitting || p.items.length === 0,
              className: 'gap-2 bg-indigo-600 hover:bg-indigo-700',
              'data-testid': 'dispatch-dc-btn'
            },
              p.submitting ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : React.createElement(Truck, { className: 'w-4 h-4' }),
              p.submitting ? 'Saving...' : 'Save & Dispatch'))))));
}

function renderHistory(dcs, loading, selectedDCs, toggleSelect, toggleSelectAll, handleBulkDelete, handleDelete, handleReceive, downloadPDF, setViewDC, setViewOpen, fetchData, startEditDraft, handleFinalize) {
  function statusBadgeClass(s) {
    if (s === 'draft') return 'bg-slate-100 text-slate-700 border-slate-300';
    if (s === 'received') return 'bg-green-100 text-green-800';
    return 'bg-blue-100 text-blue-800'; // dispatched
  }
  return React.createElement('div', { className: 'space-y-3' },
    React.createElement('div', { className: 'flex gap-2' },
      React.createElement(Button, { variant: 'outline', size: 'sm', onClick: fetchData, className: 'gap-1' }, React.createElement(RefreshCw, { className: 'w-4 h-4' }), 'Refresh'),
      selectedDCs.length > 0 && React.createElement(Button, { variant: 'outline', size: 'sm', onClick: handleBulkDelete, className: 'gap-1 border-red-300 text-red-600 hover:bg-red-50' }, React.createElement(Trash2, { className: 'w-4 h-4' }), 'Delete Selected (' + selectedDCs.length + ')')),
    loading ? React.createElement('div', { className: 'text-center py-12' }, React.createElement(Loader2, { className: 'w-8 h-8 animate-spin mx-auto text-gray-400' })) :
    dcs.length === 0 ? React.createElement('div', { className: 'text-center py-12 text-gray-400' }, React.createElement(Truck, { className: 'w-12 h-12 mx-auto mb-3' }), React.createElement('p', { className: 'font-medium' }, 'No delivery challans yet')) :
    React.createElement('div', { className: 'border rounded-lg overflow-x-auto' },
      React.createElement('table', { className: 'w-full text-sm' },
        React.createElement('thead', { className: 'bg-slate-700 text-white' },
          React.createElement('tr', null,
            React.createElement('th', { className: 'px-3 py-2.5 w-10' }, React.createElement('input', { type: 'checkbox', checked: selectedDCs.length === dcs.length && dcs.length > 0, onChange: toggleSelectAll, className: 'w-4 h-4' })),
            React.createElement('th', { className: 'px-3 py-2.5 text-left font-semibold text-xs uppercase tracking-wide' }, 'DC Number'),
            React.createElement('th', { className: 'px-3 py-2.5 text-left font-semibold text-xs uppercase tracking-wide' }, 'From'),
            React.createElement('th', { className: 'px-3 py-2.5 text-left font-semibold text-xs uppercase tracking-wide' }, 'To'),
            React.createElement('th', { className: 'px-3 py-2.5 text-center font-semibold text-xs uppercase tracking-wide' }, 'Items'),
            React.createElement('th', { className: 'px-3 py-2.5 text-center font-semibold text-xs uppercase tracking-wide' }, 'Qty'),
            React.createElement('th', { className: 'px-3 py-2.5 text-right font-semibold text-xs uppercase tracking-wide' }, 'Value'),
            React.createElement('th', { className: 'px-3 py-2.5 text-center font-semibold text-xs uppercase tracking-wide' }, 'Status'),
            React.createElement('th', { className: 'px-3 py-2.5 text-center font-semibold text-xs uppercase tracking-wide' }, 'Date'),
            React.createElement('th', { className: 'px-3 py-2.5 text-center font-semibold text-xs uppercase tracking-wide' }, 'Actions'))),
        React.createElement('tbody', { className: 'divide-y' },
          dcs.map(function(dc) {
            var isSel = selectedDCs.indexOf(dc.id) >= 0;
            return React.createElement('tr', { key: dc.id, className: 'hover:bg-gray-50 ' + (isSel ? 'bg-blue-50' : '') },
              React.createElement('td', { className: 'px-3 py-2' }, React.createElement('input', { type: 'checkbox', checked: isSel, onChange: function() { toggleSelect(dc.id); }, className: 'w-4 h-4' })),
              React.createElement('td', { className: 'px-3 py-2 font-mono font-semibold text-indigo-600' }, dc.dc_number),
              React.createElement('td', { className: 'px-3 py-2' }, dc.from_location),
              React.createElement('td', { className: 'px-3 py-2' },
                React.createElement('div', null, dc.to_location),
                dc.to_franchise_id && React.createElement('div', { className: 'text-xs text-slate-400' }, dc.to_franchise_id)),
              React.createElement('td', { className: 'px-3 py-2 text-center' }, (dc.items || []).length),
              React.createElement('td', { className: 'px-3 py-2 text-center font-semibold' }, dc.total_quantity),
              React.createElement('td', { className: 'px-3 py-2 text-right font-semibold text-indigo-700' }, '₹', (dc.total_value || 0).toFixed(2)),
              React.createElement('td', { className: 'px-3 py-2 text-center' },
                React.createElement(Badge, { className: statusBadgeClass(dc.status) }, (dc.status || '').toUpperCase())),
              React.createElement('td', { className: 'px-3 py-2 text-center text-xs' }, dc.created_at ? dc.created_at.split('T')[0] : ''),
              React.createElement('td', { className: 'px-3 py-2 text-center' },
                React.createElement('div', { className: 'flex justify-center gap-1' },
                  React.createElement(Button, { variant: 'ghost', size: 'sm', onClick: function() { setViewDC(dc); setViewOpen(true); } }, React.createElement(Eye, { className: 'w-4 h-4' })),
                  React.createElement(Button, { variant: 'ghost', size: 'sm', onClick: function() { downloadPDF(dc.id, dc.dc_number); }, title: dc.status === 'draft' ? 'Download draft PDF' : 'Download PDF', 'data-testid': 'download-dc-' + dc.id }, React.createElement(Download, { className: 'w-4 h-4' })),
                  dc.status === 'draft' && React.createElement(Button, { variant: 'ghost', size: 'sm', onClick: function() { startEditDraft(dc); }, className: 'text-amber-600', title: 'Edit draft', 'data-testid': 'edit-dc-' + dc.id }, React.createElement(FileText, { className: 'w-4 h-4' })),
                  dc.status === 'draft' && React.createElement(Button, { variant: 'ghost', size: 'sm', onClick: function() { handleFinalize(dc.id); }, className: 'text-indigo-600', title: 'Save & Dispatch', 'data-testid': 'finalize-draft-' + dc.id }, React.createElement(Truck, { className: 'w-4 h-4' })),
                  dc.status === 'dispatched' && React.createElement(Button, { variant: 'ghost', size: 'sm', onClick: function() { handleReceive(dc.id); }, className: 'text-green-600', title: 'Mark Received' }, React.createElement(CheckCircle2, { className: 'w-4 h-4' })),
                  dc.status === 'draft' && React.createElement(Button, { variant: 'ghost', size: 'sm', onClick: function() { handleDelete(dc.id); }, className: 'text-red-500 hover:text-red-700', 'data-testid': 'delete-dc-' + dc.id }, React.createElement(Trash2, { className: 'w-4 h-4' })),
                  dc.status !== 'draft' && React.createElement('span', { className: 'inline-flex items-center gap-1 text-[10px] font-semibold text-slate-400 px-1', title: 'Finalized (Locked) — editing and deletion are disabled', 'data-testid': 'locked-dc-' + dc.id }, React.createElement(Lock, { className: 'w-3.5 h-3.5' }), 'Locked'))));
          })))));
}

function DCViewDialog({ dc, open, onOpenChange }) {
  if (!dc) return null;
  return React.createElement(Dialog, { open: open, onOpenChange: onOpenChange },
    React.createElement(DialogContent, { className: 'max-w-2xl max-h-[80vh] overflow-y-auto' },
      React.createElement(DialogHeader, null, React.createElement(DialogTitle, { className: 'flex items-center gap-2' }, React.createElement(Truck, { className: 'w-5 h-5 text-indigo-600' }), 'DC ', dc.dc_number)),
      React.createElement('div', { className: 'space-y-4 py-4 text-sm' },
        React.createElement('div', { className: 'grid grid-cols-2 gap-3' },
          React.createElement('div', null, 'From: ', React.createElement('strong', null, dc.from_location)),
          React.createElement('div', null, 'To: ', React.createElement('strong', null, dc.to_location), dc.to_franchise_id ? ' (' + dc.to_franchise_id + ')' : ''),
          React.createElement('div', null, 'Status: ', React.createElement(Badge, { className: dc.status === 'draft' ? 'bg-slate-100 text-slate-700 border-slate-300' : (dc.status === 'received' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800') }, (dc.status || '').toUpperCase())),
          React.createElement('div', null, 'Date: ', dc.created_at ? dc.created_at.split('T')[0] : '')),
        dc.to_address && React.createElement('div', { className: 'p-2 bg-slate-50 rounded border text-xs' },
          React.createElement('strong', null, 'Ship To: '), dc.to_address,
          dc.to_contact_person && React.createElement('div', { className: 'mt-1' }, 'Contact: ', dc.to_contact_person, dc.to_phone ? ' · ' + dc.to_phone : '')),
        React.createElement('div', { className: 'border rounded-lg overflow-x-auto' },
          React.createElement('table', { className: 'w-full text-sm' },
            React.createElement('thead', { className: 'bg-indigo-50' },
              React.createElement('tr', null,
                React.createElement('th', { className: 'px-3 py-2 text-left' }, 'Product'),
                React.createElement('th', { className: 'px-3 py-2 text-center' }, 'Qty'),
                React.createElement('th', { className: 'px-3 py-2 text-right' }, 'MRP'),
                React.createElement('th', { className: 'px-3 py-2 text-right' }, 'SP'),
                React.createElement('th', { className: 'px-3 py-2 text-right' }, 'Total'))),
            React.createElement('tbody', { className: 'divide-y' },
              (dc.items || []).map(function(item, i) {
                return React.createElement('tr', { key: i },
                  React.createElement('td', { className: 'px-3 py-2' }, item.product_name),
                  React.createElement('td', { className: 'px-3 py-2 text-center font-semibold' }, item.quantity, ' ', item.unit),
                  React.createElement('td', { className: 'px-3 py-2 text-right' }, '₹', (item.mrp || 0).toFixed(2)),
                  React.createElement('td', { className: 'px-3 py-2 text-right' }, '₹', (item.selling_price || 0).toFixed(2)),
                  React.createElement('td', { className: 'px-3 py-2 text-right font-semibold' }, '₹', (item.total_value || 0).toFixed(2)));
              }),
              React.createElement('tr', { className: 'bg-indigo-50 font-bold' },
                React.createElement('td', { className: 'px-3 py-2', colSpan: 4 }, 'Grand Total'),
                React.createElement('td', { className: 'px-3 py-2 text-right text-indigo-700' }, '₹', (dc.total_value || 0).toFixed(2)))))),
        dc.remarks && React.createElement('div', null, React.createElement('strong', null, 'Remarks: '), dc.remarks))));
}
